import {PerformanceLogger} from '../performance';
import {throttle} from 'lodash-es';
import percentile from 'percentile';
import {average} from './utils';
import {LOG_DOC_TYPE, LOG_TYPE} from './const';
import {ENVIRONMENT_LEVEL} from '@/Constant';
import {get, set, remove} from '@/utils/storageUtils';
import {getCurDocument} from '@/store';
import {DOC_TYPE} from '@/constants/document/doc';

const LOCAL_STORAGE_KEY = 'input_delay';
const THROTTLE_TIME = 200;
const LOG_TIME = 500;
const MAX_DIFF_TIME = 2 * 60 * 1000;
export class EditorInputDelay {

    key = 'EditorInputDelay';

    logger: PerformanceLogger | null = null;

    keydownCount: number = 0;

    throttleCalculateDelay: (delay: number) => void;

    canLog: boolean = true;

    constructor(logger: PerformanceLogger) {
        const logLevel = logger?.logLevel || '';
        this.canLog = [
            ENVIRONMENT_LEVEL.online,
            ENVIRONMENT_LEVEL.gray,
            ENVIRONMENT_LEVEL.qa,
        ].includes(logLevel);
        // 解决ts报错
        this.throttleCalculateDelay = throttle(this.calculateDelay, THROTTLE_TIME);
        this.logger = logger;
        this.bindUpload();
        // 用户直接关闭页面时会上报一次
        window.addEventListener('beforeunload', () => {
            const curDocument = getCurDocument();
            const isSheet = curDocument?.type === DOC_TYPE.SHEET;
            this.bindUpload(isSheet);
        });
    }

    // 上报输入延迟
    bindUpload(isSheet?: boolean) {
        const logger = this.logger;
        if (this.canLog) {
            const delay = get(LOCAL_STORAGE_KEY);
            const opVersion = logger?.getOpVersionInfo();
            if (!delay) {
                return;
            }
            if (delay.delay <= 0) {
                return;
            }
            const log = {
                id: '80001.8.37',
                data: {
                    delay: delay.delay,
                    keydownCount: delay.keydownCount,
                    opVersion, // 表格没有这个值
                    docType: isSheet ? LOG_DOC_TYPE.sheet : LOG_DOC_TYPE.doc,
                },
            };
            logger?.feLogger(log);
            remove(LOCAL_STORAGE_KEY);
        }
    }

    inputEvent = (event: Event) => {
        const logger = this.logger;
        const buffer = logger?.buffer;
        // firefox 可能会返回0 影响数据 先兼容掉
        if (!buffer || !event.timeStamp) {
            return;
        }
        this.keydownCount++;
        const diff = Number(performance.now() - event.timeStamp);
        this.throttleCalculateDelay(diff);
    };

    calculateDelay = (diff: number, docType?: string) => {
        const logger = this.logger;
        const buffer = logger?.buffer;
        // firefox 可能会返回0 影响数据 先兼容掉
        if (!buffer) {
            return;
        }

        if (diff > MAX_DIFF_TIME) {
            return;
        }
        // 线下记录原始数据
        // eslint-disable-next-line no-negated-condition
        const logLevel = logger?.logLevel || '';
        if (logLevel !== ENVIRONMENT_LEVEL.online) {
            buffer.add(this.key, diff);
        }
        // 线上、灰度、qa环境 存储平均值到localStorage 等待时机上报
        if (this.canLog) {
            this.saveWebLogDataToLocalStorage(diff);
            // 如果超过阀值 上报埋点 携带当前时间戳
            if (diff > LOG_TIME) {
                this.logLargeDelay(diff, docType);
            }
        }
    };

    logLargeDelay = (delay: number, docType?: string) => {
        const logger = this.logger;
        logger?.feLogger({
            id: '80001.8.46',
            data: {
                delay: delay,
                timestamp: +new Date(),
                docType, // 1.docType === 'sheet' 表格;  2.docType 不上报，非表格
            },
        });
    };

    saveWebLogDataToLocalStorage = (diff: number) => {
        const logger = this.logger;
        const buffer = logger?.buffer;
        if (!buffer) {
            return;
        }
        const weblogKey = `${this.key}-weblog`;
        const {delay, count} = buffer?.get(weblogKey) || {
            delay: 0,
            count: 0,
        };
        buffer.set(weblogKey, {
            delay: ((delay * count) + diff) / ((+count) + 1),
            count: (+count) + 1,
            keydownCount: this.keydownCount,
        });
        set(LOCAL_STORAGE_KEY, buffer.get(weblogKey));
    };

    bind() {
        setTimeout(() => {
            this.keydownCount = 0;
            document.addEventListener('keydown', this.inputEvent);
            // document.addEventListener('click', this.throttleInputEvent);
        }, 0);
    }
    unbind() {
        document.removeEventListener('keydown', this.inputEvent);
        // document.removeEventListener('click', this.throttleInputEvent);
        this.bindUpload();
    }

    getData() {
        const logger = this.logger;
        if (!logger) {
            return null;
        }
        const logs = logger.buffer.get(this.key);
        if (!logs || !logs.length) {
            return null;
        }
        const calculatedData = percentile<number>(
            [80, 85, 90, 95],
            logs
        );
        const data = calculatedData as number[];
        return {
            type: this.key,
            logType: LOG_TYPE.period,
            percentile: {
                '80': data[0],
                '85': data[1],
                '90': data[2],
                '95': data[3],
            },
            average: average(logs),
            origin: logs,
        };
    }
}
